import { useState } from "react";
import { Send } from "lucide-react";
import { z } from "zod";
import { submitLead } from "@/lib/submissions";
import { ConsentCheckbox, DocReviewCheckbox } from "@/components/consent-checkbox";
import { trackEvent } from "@/lib/analytics";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Enter a valid work email").max(255),
  company: z.string().trim().min(1, "Company is required").max(120),
  headcount: z.string().max(20),
  topic: z.string().max(120),
  message: z.string().trim().min(1, "Tell us briefly what's going on").max(1000),
});

const topicsByIndustry: Record<string, string[]> = {
  hospitality: ["Tronc & tipping", "NMW compliance", "Seasonal contracts", "Grievance / dismissal", "Other"],
  construction: ["CIS / worker status", "TUPE on contract change", "Site discipline", "Tribunal claim", "Other"],
  healthcare: ["CQC / safer recruitment", "Clinical conduct", "Sponsor licence", "TUPE", "Other"],
  "professional-services": ["Restrictive covenants", "Partner / director exit", "Team move", "Bonus dispute", "Other"],
  "retail-ecommerce": ["Store closure / redundancy", "Collective consultation", "Peak-season hiring", "Warehouse discipline", "Other"],
  manufacturing: ["Shift restructure", "Union recognition", "Working time", "Long-term sickness", "Other"],
  "charities-non-profits": [
    "Volunteer status & agreements",
    "Funding-linked fixed terms",
    "Trustee & governance issues",
    "TUPE on contract retender",
    "Restructure / redundancy",
    "Other",
  ],
  "care-homes": [
    "CQC & safer recruitment",
    "Sponsor licence & right to work",
    "Night working & working time",
    "Safeguarding investigation",
    "Sickness & absence",
    "Other",
  ],
};

const defaultTopics = ["HR advice", "Employment law issue", "Restructure / redundancy", "Tribunal claim", "Other"];

interface IndustryLeadFormProps {
  industrySlug: string;
  industryName: string;
}

export function IndustryLeadForm({ industrySlug, industryName }: IndustryLeadFormProps) {
  const topics = topicsByIndustry[industrySlug] ?? defaultTopics;
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    headcount: "1-49",
    topic: topics[0],
    message: `I work in the ${industryName.toLowerCase()} sector and would like to discuss `,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const update =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const [consent, setConsent] = useState(false);
  const [docReview, setDocReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string") fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setServerError(null);
    try {
      await submitLead({
        docReview,
        ...result.data,
        consent,
        source: "industry",
        industry_slug: industrySlug,
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      trackEvent("form_submit", {
        category: "lead",
        action: "industry_enquiry",
        label: industryName,
        properties: {
          form_type: industrySlug,
          industry: industryName,
          topic: result.data.topic,
          headcount: result.data.headcount,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setServerError(
        err instanceof Error && err.message.startsWith("Please tick")
          ? err.message
          : "We couldn't send your enquiry. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-accent/10 text-accent">
          ✓
        </div>
        <h3 className="font-serif text-2xl text-brand">Thank you.</h3>
        <p className="mt-3 text-sm text-muted-foreground">
          A senior advisor with {industryName.toLowerCase()} experience will be in touch within two business hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-2xl border border-border bg-card p-6 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-widest text-accent">Ask an HR expert</p>
      <h3 className="mt-3 font-serif text-2xl text-brand">
        Speak to a {industryName.toLowerCase()} specialist.
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Sector-aware advice from a senior consultant. Reply within two business hours.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Your name" error={errors.name}>
          <input
            value={form.name}
            onChange={update("name")}
            maxLength={100}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>
        <Field label="Work email" error={errors.email}>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            maxLength={255}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>
        <Field label="Company" error={errors.company}>
          <input
            value={form.company}
            onChange={update("company")}
            maxLength={120}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>
        <Field label="Headcount">
          <select
            value={form.headcount}
            onChange={update("headcount")}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
          >
            <option>1-9</option>
            <option>10-49</option>
            <option>50-249</option>
            <option>250+</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Sector">
            <input
              readOnly
              value={industryName}
              aria-label="Selected sector"
              className="w-full cursor-default rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-foreground"
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={`Topic (${industryName})`}>
            <select
              value={form.topic}
              onChange={update("topic")}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none"
            >
              {topics.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Tell us more" error={errors.message}>
            <textarea
              rows={4}
              value={form.message}
              onChange={update("message")}
              maxLength={1000}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </Field>
        </div>
      </div>

      {serverError && <p className="mt-4 text-sm text-destructive">{serverError}</p>}
      <ConsentCheckbox id="consent-industry" checked={consent} onChange={setConsent} className="mt-6" />
      <DocReviewCheckbox id="doc-review-industry" checked={docReview} onChange={setDocReview} />
      <button
        type="submit"
        disabled={submitting}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand py-3 text-sm font-medium text-brand-foreground transition hover:brightness-110 disabled:opacity-60 sm:w-auto sm:px-8"
      >
        <Send className="size-4" />
        {submitting ? "Sending…" : `Send to a ${industryName.toLowerCase()} specialist`}
      </button>
      <p className="mt-3 text-xs text-muted-foreground">
        Your enquiry is tagged <span className="font-medium text-foreground">{industryName}</span> and routed to an
        advisor with sector experience.
      </p>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-foreground">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}
